import { CurrencyPipe, DecimalPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AmountPipe } from '../../../core/pipes/amount.pipe';
import { CountBadgePipe } from '../../../core/pipes/badge.pipe';
import { AuthService } from '../../auth/auth.service';
import { FilterConfig, GlobalFilterPanelComponent } from '../components/global-filter-panel/global-filter-panel.component';
import { DashboardService } from '../dashboard.service';
import { DashboardEngineService } from '../services/dashboard-engine.service';
import { ActivityTimelineComponent } from '../widgets/shared/activity-timeline/activity-timeline.component';
import { ApprovalQueueListComponent } from '../widgets/shared/approval-queue-list/approval-queue-list.component';
import { PaymentMethodsCardComponent } from '../widgets/shared/payment-methods-card/payment-methods-card.component';
import { SubscriptionBillingCardComponent } from '../widgets/shared/subscription-billing-card/subscription-billing-card.component';

@Component({
  selector: 'app-finance-dashboard',
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    AmountPipe,
    CountBadgePipe,
    CurrencyPipe,
    DecimalPipe,
    NgClass,
    GlobalFilterPanelComponent,
    ApprovalQueueListComponent,
    ActivityTimelineComponent,
    SubscriptionBillingCardComponent,
    PaymentMethodsCardComponent,
  ],
  templateUrl: './finance-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { style: 'display: block; width: 100%;' },
})
export class FinanceDashboardComponent {
  private engine = inject(DashboardEngineService);
  private auth = inject(AuthService);
  protected dashboardSvc = inject(DashboardService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  readonly dashboardType = 'finance';
  filters: any = {};

  arData: any = null;
  apData: any = null;
  collectionData: any = null;
  budgetData: any = null;
  overdueData: any = null;

  loadingAr = false;
  loadingAp = false;
  loadingCollection = false;
  loadingBudget = false;
  loadingOverdue = false;

  readonly filterConfig: FilterConfig = {
    community: true,
    period: true,
    dateRange: false,
  };

  get hasArPermission(): boolean {
    return this.auth.canAccess(['accounting_invoices_view']);
  }
  get hasApPermission(): boolean {
    return this.auth.canAccess(['accounting_bills_view']);
  }
  get hasCollectionPermission(): boolean {
    return this.auth.canAccess(['accounting_receipts_view']);
  }
  get hasBudgetPermission(): boolean {
    return this.auth.canAccess(['accounting_budgets_view']);
  }

  onFiltersChanged(f: any): void {
    this.filters = { ...f };
    this.dashboardSvc.activeFilters.set(this.filters);
    this.loadAll();
    this.cdr.markForCheck();
  }

  private loadAll(): void {
    this.loadAr();
    this.loadAp();
    this.loadCollection();
    this.loadBudget();
    this.loadOverdue();
  }

  private loadAr(): void {
    if (!this.hasArPermission) return;
    this.loadingAr = true;
    this.engine
      .getWidgetData(this.dashboardType, 'arSummary', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.arData = res.record?.data ?? {};
          this.loadingAr = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingAr = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadAp(): void {
    if (!this.hasApPermission) return;
    this.loadingAp = true;
    this.engine
      .getWidgetData(this.dashboardType, 'apSummary', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.apData = res.record?.data ?? {};
          this.loadingAp = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingAp = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadCollection(): void {
    if (!this.hasCollectionPermission) return;
    this.loadingCollection = true;
    this.engine
      .getWidgetData(this.dashboardType, 'collectionSummary', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.collectionData = res.record?.data ?? {};
          this.loadingCollection = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingCollection = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadBudget(): void {
    if (!this.hasBudgetPermission) return;
    this.loadingBudget = true;
    this.engine
      .getWidgetData(this.dashboardType, 'budgetVsActual', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.budgetData = res.record?.data ?? {};
          this.loadingBudget = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingBudget = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadOverdue(): void {
    if (!this.hasArPermission) return;
    this.loadingOverdue = true;
    this.engine
      .getWidgetData(this.dashboardType, 'overdueReceivables', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.overdueData = res.record?.data ?? {};
          this.loadingOverdue = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingOverdue = false;
          this.cdr.markForCheck();
        },
      });
  }

  getBudgetPctColor(pct: number): string {
    if (pct >= 100) return 'bg-red-500';
    if (pct >= 90) return 'bg-orange-500';
    if (pct >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  }

  getAgingColor(days: number): string {
    if (days > 60) return 'text-red-600 bg-red-50';
    if (days > 30) return 'text-orange-600 bg-orange-50';
    return 'text-yellow-600 bg-yellow-50';
  }
}
